import { useState } from 'react'
import {
  Check,
  CloudOff,
  Copy,
  FileWarning,
  Lock,
  RefreshCw,
  SearchX,
  ServerCrash,
  ShieldX,
  TimerOff,
  TriangleAlert,
  type LucideIcon,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { isApiError, type ErrorKind } from '@/lib/http'
import { cn } from '@/lib/utils/cn'

interface Presentation {
  icon: LucideIcon
  title: string
  description: string
}

const PRESENTATIONS: Record<ErrorKind, Presentation> = {
  network: {
    icon: CloudOff,
    title: 'Servidor indisponível',
    description: 'Não foi possível conectar ao Woctools. Verifique a conexão e tente novamente.',
  },
  timeout: {
    icon: TimerOff,
    title: 'A consulta demorou demais',
    description: 'O servidor não respondeu dentro do tempo limite. Tente novamente em instantes.',
  },
  invalid_response: {
    icon: FileWarning,
    title: 'Resposta fora do contrato',
    description:
      'O servidor respondeu em um formato diferente do esperado. Informe o request id ao suporte.',
  },
  unauthorized: {
    icon: Lock,
    title: 'Sessão expirada',
    description: 'Sua sessão não é mais válida. Faça login novamente para continuar.',
  },
  forbidden: {
    icon: ShieldX,
    title: 'Acesso negado',
    description: 'Seu perfil não tem permissão para esta operação.',
  },
  not_found: {
    icon: SearchX,
    title: 'Recurso não encontrado',
    description: 'O item solicitado não existe ou foi removido.',
  },
  conflict: {
    icon: TriangleAlert,
    title: 'Conflito de dados',
    description: 'O recurso foi alterado por outra operação. Recarregue e tente novamente.',
  },
  validation: {
    icon: FileWarning,
    title: 'Dados inválidos',
    description: 'O servidor recusou os dados enviados.',
  },
  server: {
    icon: ServerCrash,
    title: 'Erro no servidor',
    description: 'O backend falhou ao processar a requisição. Informe o request id ao suporte.',
  },
  unknown: {
    icon: ServerCrash,
    title: 'Não foi possível carregar os dados',
    description: 'Ocorreu um erro inesperado.',
  },
}

/**
 * Códigos genéricos do backend v2 cuja mensagem vem em inglês e descreve a infraestrutura, não a tela:
 * a UI mostra o texto em português e mantém a mensagem original como detalhe para o suporte.
 */
const CODE_PRESENTATIONS: Record<
  string,
  Pick<Presentation, 'title' | 'description'> & { keepMessage?: boolean }
> = {
  dependency_not_configured: {
    title: 'Integração não configurada',
    description:
      'Uma integração necessária não está configurada no servidor. Informe o request id ao suporte.',
  },
  // a mensagem do backend diz qual serviço falhou e por quê (ex.: o motivo do Argo): continua sendo o texto
  upstream_error: {
    title: 'Serviço externo com falha',
    description:
      'Um serviço usado pelo Woctools falhou ou não respondeu. Tente novamente em instantes.',
    keepMessage: true,
  },
  service_unavailable: {
    title: 'Serviço temporariamente indisponível',
    description: 'Tente novamente em instantes.',
  },
  database_error: {
    title: 'Falha no banco de dados do Woctools',
    description:
      'O servidor não conseguiu concluir a operação no banco de dados. Informe o request id ao suporte.',
  },
}

/** Mensagens padrão (inglês) do backend para erros sem contexto: trocadas pelo texto do tipo de erro. */
const GENERIC_BACKEND_MESSAGES = new Set([
  'Authentication required.',
  'Invalid or expired token.',
  'You do not have permission to perform this action.',
  'Resource not found.',
  'Resource state conflict.',
  'Invalid request.',
  'Request validation failed.',
  'Internal server error.',
  'Too many requests.',
])

interface ErrorStateProps {
  error: unknown
  onRetry?: () => void
  /** Sobrescreve o título padrão do tipo de erro. */
  title?: string
  className?: string
  compact?: boolean
}

function RequestIdLine({ requestId }: { requestId: string }) {
  const [copied, setCopied] = useState(false)
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(requestId)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
    } catch {
      // Clipboard indisponível (ex.: contexto não seguro): o id continua visível para copiar à mão.
    }
  }
  return (
    <p className="mt-2 inline-flex items-center gap-1.5 font-mono text-xs text-muted-foreground">
      <span>
        request id: <span data-testid="error-request-id">{requestId}</span>
      </span>
      <button
        type="button"
        onClick={copy}
        aria-label="Copiar request id"
        className="rounded p-0.5 transition-colors hover:bg-muted hover:text-foreground"
      >
        {copied ? (
          <Check className="h-3 w-3" aria-hidden />
        ) : (
          <Copy className="h-3 w-3" aria-hidden />
        )}
      </button>
    </p>
  )
}

export function ErrorState({ error, onRetry, title, className, compact }: ErrorStateProps) {
  const apiError = isApiError(error) ? error : null
  const kind: ErrorKind = apiError?.kind ?? 'unknown'
  const presentation = PRESENTATIONS[kind]
  const Icon = presentation.icon
  const byCode = apiError ? CODE_PRESENTATIONS[apiError.code] : undefined
  const message = apiError?.message
  const replace = byCode && !byCode.keepMessage ? byCode : undefined
  const description = replace
    ? replace.description
    : message && !GENERIC_BACKEND_MESSAGES.has(message)
      ? message
      : (byCode?.description ?? presentation.description)
  const detail = replace && message ? message : null
  const retryable = apiError ? apiError.retryable : true

  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-center gap-3 rounded-xl border border-amber-200/70 bg-amber-50/60 px-6 text-center dark:border-amber-900/50 dark:bg-amber-950/20',
        compact ? 'py-6' : 'py-12',
        className,
      )}
    >
      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/40">
        <Icon className="h-5 w-5 text-amber-700 dark:text-amber-400" aria-hidden />
      </span>
      <div>
        <p className="text-sm font-semibold">{title ?? byCode?.title ?? presentation.title}</p>
        <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">{description}</p>
        {detail ? (
          <p className="mx-auto mt-1 max-w-md text-xs text-muted-foreground/80">
            Detalhe técnico: {detail}
          </p>
        ) : null}
        {apiError?.requestId ? <RequestIdLine requestId={apiError.requestId} /> : null}
      </div>
      {onRetry && retryable ? (
        <Button variant="outline" size="sm" onClick={onRetry}>
          <RefreshCw className="h-3.5 w-3.5" aria-hidden />
          Tentar novamente
        </Button>
      ) : null}
    </div>
  )
}
